import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { parseLocalDate } from "@/lib/date";
import { Calendar as CalendarIcon, ArrowRight, Home as HomeIcon, Waves, Trophy, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ICONS = {
  reservation: HomeIcon,
  community: Users,
  board: Users,
  event: CalendarIcon,
};

export default function CommunityCalendarWidget() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/calendar/events", { params: { limit: 6 } })
      .then(({ data }) => setEvents(data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Community Calendar</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1A2421]">What's coming up.</h2>
        </div>
        <Link to="/amenities">
          <Button data-testid="view-calendar-btn" variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white">
            View Full Calendar <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
      {loading ? (
        <div className="text-[#5C6A64]">Loading events…</div>
      ) : events.length === 0 ? (
        <Card className="rounded-2xl border-[#D5CFC4]"><CardContent className="p-8 text-center text-[#5C6A64]">
          No upcoming events yet. Check back soon or <Link to="/announcements" className="underline text-[#C86650]">submit one</Link>.
        </CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-calendar">
          {events.map((e) => {
            const Icon = ICONS[e.type] || CalendarIcon;
            const dateObj = parseLocalDate(e.date) || new Date();
            return (
              <Card key={e.id || `${e.date}-${e.title}-${e.time_slot || ''}`} data-testid={`calendar-event-${e.id || e.date}`} className="rounded-2xl border-[#D5CFC4] hover:-translate-y-1 transition-transform duration-300">
                <CardContent className="p-6 flex gap-4">
                  <div className="flex-shrink-0 w-16 text-center">
                    <p className="text-xs uppercase tracking-wider text-[#C86650]">{dateObj.toLocaleString("default", { month: "short" })}</p>
                    <p className="font-serif text-3xl text-[#2C4C3B]">{dateObj.getDate()}</p>
                  </div>
                  <div className="flex-1 border-l border-[#D5CFC4] pl-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-[#C86650]" />
                      <span className="text-xs uppercase tracking-wider text-[#5C6A64]">{e.type}</span>
                    </div>
                    <p className="font-medium text-[#1A2421]">{e.title}</p>
                    {e.time && <p className="text-xs text-[#5C6A64] mt-1">{e.time}</p>}
                    {e.description && <p className="text-xs text-[#5C6A64] mt-1 line-clamp-2">{e.description}</p>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
